import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";

import * as Sentry from "@sentry/react";
import { PostHogProvider } from "posthog-js/react";
import { SupabaseClient } from "@supabase/supabase-js";
import { supabaseIntegration } from "@supabase/sentry-js-integration";
import {
  redactSentryEventUrls,
  redactPosthogProperties,
} from "@/products/shared/utils/redactSensitiveUrls";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;

Sentry.init({
  dsn: "https://757e47827c34ce51cdcf82f2df7c6346@o4510631013187584.ingest.us.sentry.io/4510631015088128",
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  beforeSend: (event) => redactSentryEventUrls(event),
  beforeSendTransaction: (event) => redactSentryEventUrls(event),
  integrations: [
    // Supabase integration for monitoring Supabase client errors
    supabaseIntegration(SupabaseClient, Sentry, {
      tracing: true,
      breadcrumbs: true,
      errors: true,
    }),
    // Deduplicate spans - skip Supabase requests in browser tracing
    Sentry.browserTracingIntegration({
      shouldCreateSpanForRequest: (url) => {
        return !url.startsWith(`${SUPABASE_URL}/rest`);
      },
    }),
  ],
});


// Set up global error handlers for unhandled promise rejections
window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
  const reason = event.reason;
  Sentry.captureException(
    reason instanceof Error ? reason : new Error(String(reason)),
    {
      contexts: {
        promise: {
          rejection_reason: String(reason),
        },
      },
      tags: {
        error_type: 'unhandled_promise_rejection',
      },
    }
  );
  // Log to console as well
  console.error('[Global Handler] Unhandled promise rejection:', reason);
});

const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY as string;
const posthogHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST as string;

const options = {
  api_host: posthogHost,
  sanitize_properties: (properties: Record<string, unknown>) =>
    redactPosthogProperties(properties),
};

const container = document.getElementById("root")!;
const root = createRoot(container);

// Error fallback component for Sentry ErrorBoundary
const ErrorFallback = ({ error, resetError }: { error: Error; resetError: () => void }) => {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Something went wrong</h2>
        <p className="text-gray-600 mb-4">
          We've been notified of this error and will look into it. Please try refreshing the page.
        </p>
        <div className="space-y-2">
          <button
            onClick={resetError}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Try again
          </button>
          <button
            onClick={() => window.location.reload()}
            className="w-full px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
          >
            Reload page
          </button>
        </div>
      </div>
    </div>
  );
};

root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={ErrorFallback} showDialog={false}>
      {posthogKey && posthogHost ? (
        <PostHogProvider apiKey={posthogKey} options={options}>
          <App />
        </PostHogProvider>
      ) : (
        <App />
      )}
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);
